import { Container, Title, Text, Paper, Stack, Group, useMantineTheme, Box, Button, Breadcrumbs, Anchor, ThemeIcon, Image } from '@mantine/core';
import { useParams, Link, Navigate } from 'react-router-dom';
import { IconArrowLeft, IconCalendarEvent } from '@tabler/icons-react';
import { getTreatmentBySlug } from '../utils/treatmentData';
import { BookingButton } from '../components/BookingButton';
import { SEOHead } from '../components/SEOHead';
import { treatmentSeoData } from '../utils/treatmentSeoData';
import { useEffect } from 'react';

export function TreatmentDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const theme = useMantineTheme();

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!slug) {
    return <Navigate to="/behandlinger" replace />;
  }

  const treatment = getTreatmentBySlug(slug);
  
  if (!treatment) {
    return <Navigate to="/behandlinger" replace />;
  }

  const TreatmentIcon = treatment.icon;
  const seoData = treatmentSeoData[slug] || {
    title: treatment.title,
    description: treatment.shortDescription
  };
  
  // Helper function to render content with booking buttons
  const renderContentWithBookingButtons = (content: string) => {
    // First replace escaped newlines \\n with actual newlines \n
    const normalizedContent = content.replace(/\\n/g, '\n');
    const parts = normalizedContent.split('→ Book tid');
    return (
      <>
        {parts.map((part, partIndex) => (
          <span key={partIndex}>
            {part.split('\n').map((line, lineIndex) => (
              <span key={lineIndex}>
                {line}
                {lineIndex < part.split('\n').length - 1 && <br />}
              </span>
            ))}
            {partIndex < parts.length - 1 && (
              <Box mt="md" mb="md">
                <BookingButton size="sm" />
              </Box>
            )}
          </span>
        ))}
      </>
    );
  };

  return (
    <>
      <SEOHead 
        title={seoData.title}
        description={seoData.description}
        canonical={`/behandlinger/${slug}`}
      />
      <Container size="md" py="xl" pt={50} mx="auto" className="treatment-detail-container">
      {/* Breadcrumbs */}
      <Breadcrumbs mb="lg" className="treatment-breadcrumbs">
        <Anchor component={Link} to="/" size="sm" c="dimmed">
          Forside
        </Anchor>
        <Anchor component={Link} to="/behandlinger" size="sm" c="dimmed">
          Behandlinger
        </Anchor>
        <Text size="sm" c={theme.colors.gold[6]} fw={500}>
          {treatment.title}
        </Text>
      </Breadcrumbs>

      {/* Back Button */}
      <Button
        component={Link}
        to="/behandlinger"
        variant="light"
        leftSection={<IconArrowLeft size={16} />}
        mb="xl"
        className="treatment-back-button"
      >
        Tilbage til behandlinger
      </Button>

      {/* Main Paper */}
      <Paper 
        p="xl" 
        radius="md" 
        withBorder 
        shadow="sm"
        style={{
          background: 'rgba(255, 255, 255, 0.8)',
          borderColor: theme.colors.gold[2]
        }}
        className="treatment-detail-paper"
      >
        {/* Header */}
        <Group mb="xl" align="center" className="treatment-detail-header">
          <ThemeIcon 
            size="xl" 
            radius="50%" 
            color={treatment.color} 
            variant="light"
            className="treatment-detail-icon"
          >
            <TreatmentIcon size={28} />
          </ThemeIcon>
          <Box flex={1}>
            <Title order={1} c={theme.colors.gold[7]} className="treatment-detail-title">
              {treatment.title}
            </Title>
            <Text size="lg" c="gray.6" mt="xs" lh={1.6} className="treatment-detail-description">
              {treatment.shortDescription}
            </Text>
          </Box>
        </Group>

        {/* Treatment Image */}
        {treatment.image && (
          <Box mb="xl" className="treatment-image-container">
            <Image
              src={treatment.image}
              alt={treatment.title}
              radius="md"
              fit="cover"
              style={{
                maxHeight: 400,
                boxShadow: theme.shadows.sm
              }}
              className="treatment-detail-image"
            />
          </Box>
        )}

        {/* Content Sections */}
        <Stack gap="xl" className="treatment-detail-content">
          {treatment.sections.map((section, index) => (
            <Box key={index} className="treatment-section">
              <Title order={2} size="h3" mb="md" c={theme.colors.gold[7]} className="treatment-section-title">
                {section.title}
              </Title>
              
              {section.content && (
                <Box lh={1.6} mb="md" className="treatment-section-text">
                  {renderContentWithBookingButtons(section.content)}
                </Box>
              )}

              {section.subsections && (
                <Stack gap="md" className="treatment-subsections">
                  {section.subsections.map((subsection, subIndex) => (
                    <Box key={subIndex} className="treatment-subsection">
                      <Title order={3} size="h4" mb="sm" c={theme.colors.dark[7]} className="treatment-subsection-title">
                        {subsection.title}
                      </Title>
                      <Box lh={1.6} pl="md" className="treatment-subsection-text">
                        {renderContentWithBookingButtons(subsection.content)}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          ))}
        </Stack>

        {/* Call to Action */}
        <Paper 
          p="xl" 
          radius="md" 
          mt="xl"
          style={{
            background: `linear-gradient(135deg, ${theme.colors[treatment.color][0]} 0%, ${theme.colors[treatment.color][1]} 100%)`,
            borderColor: theme.colors[treatment.color][3]
          }}
          withBorder
          className="treatment-cta"
        >
          <Stack align="center" gap="md">
            <Button
              component="a"
              href="https://lykke-terapien.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              size="lg"
              leftSection={<IconCalendarEvent size={18} />}
              className="treatment-cta-button"
            >
              Book en tid i dag
            </Button>
          </Stack>
        </Paper>
      </Paper>

      <style>{`
        /* Mobile-first responsive design */
        @media (max-width: 768px) {
          .treatment-detail-container {
            padding: 1rem !important;
            padding-top: 60px !important;
          }
          
          .treatment-breadcrumbs {
            margin-bottom: 0.75rem !important;
          }
          
          .treatment-breadcrumbs .mantine-Text-root,
          .treatment-breadcrumbs .mantine-Anchor-root {
            font-size: 0.875rem !important;
          }
          
          .treatment-back-button {
            margin-bottom: 1.5rem !important;
            font-size: 0.875rem !important;
          }
          
          .treatment-detail-paper {
            padding: 1.5rem !important;
          }
          
          .treatment-detail-header {
            margin-bottom: 2rem !important;
            flex-direction: column !important;
            text-align: center !important;
          }
          
          .treatment-detail-icon {
            width: 60px !important;
            height: 60px !important;
            margin-bottom: 1rem !important;
          }
          
          .treatment-detail-title {
            font-size: 1.5rem !important;
            text-align: center !important;
            line-height: 1.3 !important;
          }
          
          .treatment-detail-description {
            font-size: 1rem !important;
            text-align: center !important;
            margin-top: 0.5rem !important;
          }

          .treatment-image-container {
            margin-bottom: 2rem !important;
          }

          .treatment-detail-image {
            max-height: 250px !important;
          }

          .treatment-detail-content {
            gap: 2rem !important;
          }
          
          .treatment-section-title {
            font-size: 1.25rem !important;
            margin-bottom: 1rem !important;
          }
          
          .treatment-section-text {
            font-size: 0.9rem !important;
            line-height: 1.5 !important;
            margin-bottom: 1rem !important;
          }
          
          .treatment-subsections {
            gap: 1rem !important;
          }
          
          .treatment-subsection-title {
            font-size: 1.125rem !important;
            margin-bottom: 0.5rem !important;
          }
          
          .treatment-subsection-text {
            font-size: 0.875rem !important;
            line-height: 1.5 !important;
            padding-left: 1rem !important;
          }
          
          .treatment-cta {
            padding: 1.5rem !important;
            margin-top: 2rem !important;
          }
          
          .treatment-cta-title {
            font-size: 1.25rem !important;
          }
          
          .treatment-cta-text {
            font-size: 0.9rem !important;
            margin-bottom: 0.5rem !important;
          }
          
          .treatment-cta-button {
            width: 100% !important;
            max-width: 280px !important;
          }
        }
        
        /* Tablet improvements */
        @media (min-width: 769px) and (max-width: 1024px) {
          .treatment-detail-container {
            padding: 1.5rem !important;
            padding-top: 70px !important;
          }
          
          .treatment-detail-paper {
            padding: 2rem !important;
          }
          
          .treatment-detail-title {
            font-size: 2rem !important;
          }
          
          .treatment-detail-description {
            font-size: 1.125rem !important;
          }
          
          .treatment-section-title {
            font-size: 1.5rem !important;
          }

          .treatment-detail-image {
            max-height: 320px !important;
          }
        }

        /* Enhanced visual hierarchy */
        .treatment-section {
          padding: 1.5rem 0;
          border-bottom: 1px solid ${theme.colors.gray[2]};
        }
        
        .treatment-section:last-child {
          border-bottom: none;
        }
        
        .treatment-subsection {
          margin-left: 0.5rem;
          padding-left: 1rem;
          border-left: 3px solid ${theme.colors[treatment.color][3]};
          background: rgba(255, 248, 220, 0.1);
          padding: 1rem;
          border-radius: 6px;
          margin-bottom: 1rem;
        }
        
        /* Better text formatting for lists */
        .treatment-section-text,
        .treatment-subsection-text {
          color: ${theme.colors.gray[7]} !important;
        }
        
        /* Improved button styling */
        .treatment-back-button:hover {
          transform: translateX(-2px);
          transition: all 0.2s ease;
        }
        
        .treatment-cta-button:hover {
          transform: translateY(-2px);
          transition: all 0.2s ease;
        }
        
        /* Better spacing for mobile */
        @media (max-width: 768px) {
          .treatment-section {
            padding: 1rem 0;
          }
          
          .treatment-subsection {
            margin-left: 0;
            padding: 0.75rem;
            margin-bottom: 0.75rem;
          }
        }
        
        /* Enhanced focus states */
        @media (max-width: 768px) {
          .treatment-back-button:focus,
          .treatment-cta-button:focus {
            outline: 2px solid ${theme.colors.blue[6]} !important;
            outline-offset: 2px !important;
          }
        }
      `}</style>
      </Container>
    </>
  );
}